import type { ProcessedMonthRow } from './dataParser'
import type { AdvancedFilterState } from './filters'
import { aggregateMonthlyData, type AggregatedMonthSnapshot } from './aggregateMonthly'

export type PeriodType = 'monthly' | 'quarterly' | 'half-yearly' | 'yearly'

export type SelectedSnapshot = ProcessedMonthRow | AggregatedMonthSnapshot | null

/**
 * Pick aggregated / single-month row from **already filtered** month rows (v1 UC-O0 period logic).
 */
export function computeSelectedSnapshot(
	filteredData: ProcessedMonthRow[],
	periodType: PeriodType,
	selectedQuarter: string,
	selectedHalfYear: string,
	filters: AdvancedFilterState
): SelectedSnapshot {
	const rawYears = filters.years || []
	const selectedYears = rawYears.filter(y => y > 0)
	const hasYearFilter = selectedYears.length > 0
	const yearSet = hasYearFilter ? new Set(selectedYears.map(y => Number(y))) : null

	const hasMonthFilter = filters.months && filters.months.length > 0
	const monthSet = hasMonthFilter
		? new Set(filters.months.map(m => Number(m)).filter(n => !Number.isNaN(n)))
		: null

	if (filteredData.length === 0) {
		return null
	}

	let aggregatedData: AggregatedMonthSnapshot | null = null
	let selectedMonthData: SelectedSnapshot = null

	if (periodType === 'yearly') {
		aggregatedData = aggregateMonthlyData(filteredData)
		selectedMonthData = aggregatedData
	} else if (periodType === 'half-yearly') {
		if (selectedHalfYear !== 'all') {
			const halfYearNum = parseInt(selectedHalfYear, 10)
			const halfYearData = filteredData.filter(item => {
				const inHalf =
					(parseInt(String(item.half_year_number), 10) || item.half_year_number) ===
					halfYearNum
				if (yearSet && yearSet.size > 0) {
					return inHalf && yearSet.has(Number(item.invoice_year))
				}
				return inHalf
			})
			aggregatedData =
				halfYearData.length > 0 ? aggregateMonthlyData(halfYearData) : null
			selectedMonthData =
				halfYearData.length === 0
					? null
					: aggregatedData || halfYearData[halfYearData.length - 1]
		} else {
			aggregatedData = aggregateMonthlyData(filteredData)
			selectedMonthData = aggregatedData
		}
	} else if (periodType === 'quarterly') {
		const quarterMonths: Record<string, number[]> = {
			'1': [1, 2, 3],
			'2': [4, 5, 6],
			'3': [7, 8, 9],
			'4': [10, 11, 12],
		}
		if (selectedQuarter !== 'all') {
			const monthsInQuarter = quarterMonths[selectedQuarter] || []
			const quarterData = filteredData.filter(item => {
				const itemMonth = parseInt(String(item.invoice_month), 10)
				const inQuarter = monthsInQuarter.includes(itemMonth)
				if (yearSet && yearSet.size > 0) {
					return inQuarter && yearSet.has(Number(item.invoice_year))
				}
				return inQuarter
			})
			aggregatedData =
				quarterData.length > 0 ? aggregateMonthlyData(quarterData) : null
			selectedMonthData =
				quarterData.length === 0
					? null
					: aggregatedData || quarterData[quarterData.length - 1]
		} else {
			aggregatedData = aggregateMonthlyData(filteredData)
			selectedMonthData = aggregatedData
		}
	} else if (periodType === 'monthly') {
		if (
			!hasMonthFilter &&
			((yearSet !== null && yearSet.size > 0) || !hasYearFilter)
		) {
			aggregatedData = aggregateMonthlyData(filteredData)
			selectedMonthData = aggregatedData
		} else if (hasMonthFilter && monthSet && monthSet.size > 0) {
			const monthData = filteredData.filter(item => {
				const inMonth = monthSet.has(parseInt(String(item.invoice_month), 10))
				if (yearSet && yearSet.size > 0) {
					return inMonth && yearSet.has(Number(item.invoice_year))
				}
				return inMonth
			})
			aggregatedData =
				monthData.length > 0 ? aggregateMonthlyData(monthData) : null
			selectedMonthData =
				aggregatedData || monthData[monthData.length - 1] || null
		} else {
			selectedMonthData = filteredData[filteredData.length - 1]
		}
	} else {
		selectedMonthData = filteredData[filteredData.length - 1]
	}

	return selectedMonthData
}
